/**
 * AAGUID (Authenticator Attestation GUID) Verification Module
 *
 * Extracts and verifies authenticator AAGUID from attestation objects.
 * Adapted from webauthn.js (lines 54-81) with externalized configuration.
 *
 * @module core/verifier
 */

import { SDKConfig, AuthenticatorConfig } from '../types/config';
import { decode as cborDecode } from 'cbor-x';
import { detectPlatform, type Platform } from '../utils/platform';

/**
 * Result of AAGUID verification
 *
 * @interface AAGUIDVerificationResult
 */
export interface AAGUIDVerificationResult {
  /**
   * Whether the found AAGUID matches the expected AAGUID
   */
  matches: boolean;

  /**
   * AAGUID found in the attestation object (hex string)
   */
  foundHex: string;

  /**
   * Expected AAGUID from configuration (hex string)
   */
  targetHex: string;

  /**
   * Name of the authenticator that was matched (if matches is true)
   */
  authenticatorName?: string;
}

/**
 * Error thrown when AAGUID verification fails
 *
 * @class VerificationError
 */
export class VerificationError extends Error {
  constructor(
    message: string,
    public details?: {
      cause?: unknown;
      foundHex?: string;
      targetHex?: string;
    }
  ) {
    super(message);
    this.name = 'VerificationError';
  }
}

/**
 * Converts hex string with colon separators to Uint8Array
 *
 * @param hexString - Hex string in format "XX:XX:XX:..." or "XXXX..."
 * @returns Uint8Array representation
 *
 * @throws {VerificationError} If hex string is invalid
 *
 * @example
 * ```typescript
 * const bytes = hexStringToUint8Array("00:76:63:1b");
 * // Returns: Uint8Array([0x00, 0x76, 0x63, 0x1b])
 * ```
 */
export function hexStringToUint8Array(hexString: string): Uint8Array {
  try {
    // Remove colons and spaces
    const cleaned = hexString.replace(/[:\s]/g, '');

    // Validate hex string
    if (!/^[0-9a-fA-F]+$/.test(cleaned)) {
      throw new Error('Invalid hex characters');
    }

    if (cleaned.length % 2 !== 0) {
      throw new Error('Hex string must have even length');
    }

    // Convert to Uint8Array
    const bytes = new Uint8Array(cleaned.length / 2);
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = parseInt(cleaned.substring(i * 2, i * 2 + 2), 16);
    }

    return bytes;
  } catch (error) {
    throw new VerificationError('Failed to convert hex string to Uint8Array', {
      cause: error
    });
  }
}

/**
 * Converts Uint8Array to hex string without separators
 *
 * @param bytes - Uint8Array to convert
 * @returns Hex string (lowercase, no separators)
 *
 * @example
 * ```typescript
 * const hex = uint8ArrayToHexString(new Uint8Array([0x00, 0x76, 0x63, 0x1b]));
 * // Returns: "0076631b"
 * ```
 */
export function uint8ArrayToHexString(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Resolves which AAGUIDs are acceptable for an authenticator on a given platform.
 *
 * A `platformAaguids` entry for the platform replaces the defaults outright — including when it
 * is an empty array, which means "nothing is acceptable here". That is deliberate: the fallback
 * is for platforms the config says nothing about, not a floor under an explicit statement.
 *
 * @param authenticatorConfig - The authenticator's configuration
 * @param platform - Platform the ceremony ran on
 * @returns Normalized hex AAGUIDs (lowercase, no separators), in priority order
 */
export function resolveAcceptedAAGUIDs(
  authenticatorConfig: AuthenticatorConfig,
  platform: Platform
): string[] {
  const normalize = (value: string) => uint8ArrayToHexString(hexStringToUint8Array(value));

  const platformKey = platform.toLowerCase() as keyof NonNullable<
    AuthenticatorConfig['platformAaguids']
  >;
  const perPlatform = authenticatorConfig.platformAaguids?.[platformKey];

  if (perPlatform) {
    return perPlatform.map(normalize);
  }

  return [
    authenticatorConfig.aaguid,
    ...(authenticatorConfig.additionalAaguids || [])
  ].map(normalize);
}

/**
 * Checks if the AAGUID in the attestation object matches a configured authenticator
 *
 * Extracts AAGUID from the attestation object's authenticator data and compares
 * it against the configured authenticator AAGUIDs. The AAGUID is located at
 * bytes 37-52 in the authenticator data according to WebAuthn spec.
 *
 * This is a direct adaptation of checkAAGUID() from webauthn.js (lines 54-81)
 * with the following improvements:
 * - AAGUID is externalized via config (not hardcoded)
 * - Supports multiple authenticators
 * - TypeScript types and error handling
 * - Configurable authenticator name
 *
 * @param attestationObject - The attestation object from credential creation
 * @param authenticatorName - Name of authenticator to verify against (e.g., "ultrapass")
 * @param config - SDK configuration containing authenticator AAGUIDs
 * @param platform - Platform the ceremony ran on, selecting which `platformAaguids` entry
 *   applies. Detected from the user agent when omitted; pass it explicitly from callers that
 *   already know, so the value can't disagree with the one used to build the request.
 * @returns Verification result with match status and hex values
 *
 * @throws {VerificationError} If CBOR decoding fails or authenticator not found in config
 *
 * @example
 * ```typescript
 * const result = checkAAGUID(
 *   attestationObject,
 *   'ultrapass',
 *   config
 * );
 *
 * if (result.matches) {
 *   console.log('Authenticator verified:', result.authenticatorName);
 * } else {
 *   console.error('AAGUID mismatch!');
 *   console.error('Expected:', result.targetHex);
 *   console.error('Found:', result.foundHex);
 * }
 * ```
 *
 * @see https://www.w3.org/TR/webauthn-2/#sctn-attestation
 */
export function checkAAGUID(
  attestationObject: ArrayBuffer,
  authenticatorName: string,
  config: SDKConfig,
  platform?: Platform
): AAGUIDVerificationResult {
  try {
    // Validate authenticator exists in config
    const authenticatorConfig = config.authenticators[authenticatorName];
    if (!authenticatorConfig) {
      throw new VerificationError(
        `Authenticator "${authenticatorName}" not found in configuration. ` +
          `Available: ${Object.keys(config.authenticators).join(', ')}`
      );
    }

    // Parse the attestation object to extract AAGUID
    const decodedAttestation = cborDecode(new Uint8Array(attestationObject)) as { authData: ArrayBuffer };
    const authData = decodedAttestation.authData;

    // AAGUID is at bytes 37-52 in authenticator data
    // Per WebAuthn spec: https://www.w3.org/TR/webauthn-2/#sctn-authenticator-data
    // Offset 37: AAGUID (16 bytes)
    const aaguid = new Uint8Array(authData.slice(37, 53));

    const foundHex = uint8ArrayToHexString(aaguid);

    // The AAGUID rotation reached the native builds at different times — the 1.0.13 Windows MSI
    // reports the new value while iOS/Android still report the legacy one — so which values are
    // correct depends on where the ceremony ran. Resolve per platform rather than accepting the
    // union everywhere, which would let an unexpected authenticator model pass on a platform
    // where only one value is right.
    const activePlatform = platform || detectPlatform();
    const acceptedHex = resolveAcceptedAAGUIDs(authenticatorConfig, activePlatform);

    // Reported as the expected value; the first entry is the platform's primary.
    const targetHex = acceptedHex[0] ?? '';

    const matches = acceptedHex.includes(foundHex);

    // Log for debugging (if enabled)
    if (config.debug) {
      console.log('[FIDO2-JS] AAGUID Verification:');
      console.log('  Authenticator:', authenticatorName);
      console.log('  Platform:', activePlatform);
      console.log('  Target AAGUID:', targetHex || '(none accepted on this platform)');
      if (acceptedHex.length > 1) {
        console.log('  Also accepted:', acceptedHex.slice(1).join(', '));
      }
      console.log('  Found AAGUID:', foundHex);
      console.log('  Match:', matches);
      if (matches && foundHex !== targetHex) {
        console.log('  (matched a secondary AAGUID for this platform)');
      }
    }

    return {
      matches,
      foundHex,
      targetHex,
      authenticatorName: matches ? authenticatorName : undefined
    };
  } catch (error) {
    // Re-throw VerificationError as-is
    if (error instanceof VerificationError) {
      throw error;
    }

    // Wrap other errors
    throw new VerificationError('Failed to verify AAGUID', {
      cause: error
    });
  }
}

/**
 * Checks if the AAGUID matches any configured authenticator
 *
 * Tries to match the AAGUID against all configured authenticators and
 * returns the first match found.
 *
 * @param attestationObject - The attestation object from credential creation
 * @param config - SDK configuration containing authenticator AAGUIDs
 * @returns Verification result with match status and matched authenticator name
 *
 * @throws {VerificationError} If CBOR decoding fails
 *
 * @example
 * ```typescript
 * const result = checkAAGUIDAny(attestationObject, config);
 *
 * if (result.matches) {
 *   console.log('Matched authenticator:', result.authenticatorName);
 * } else {
 *   console.error('No matching authenticator found');
 * }
 * ```
 */
export function checkAAGUIDAny(
  attestationObject: ArrayBuffer,
  config: SDKConfig,
  platform?: Platform
): AAGUIDVerificationResult {
  const authenticatorNames = Object.keys(config.authenticators);

  if (authenticatorNames.length === 0) {
    throw new VerificationError('No authenticators configured');
  }

  // Try each configured authenticator
  for (const authenticatorName of authenticatorNames) {
    const result = checkAAGUID(attestationObject, authenticatorName, config, platform);
    if (result.matches) {
      return result;
    }
  }

  // No match found - return result from first authenticator
  // (to provide foundHex and targetHex for debugging)
  const firstResult = checkAAGUID(attestationObject, authenticatorNames[0], config, platform);
  return {
    ...firstResult,
    matches: false,
    authenticatorName: undefined
  };
}
